// src/components/LearnMore.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// Firebase
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { db } from '../../firebase';

/* ---------- Skeleton for loading ---------- */
const Skeleton = () => (
  <div className="bg-gray-100 rounded-lg animate-pulse h-44" />
);

/* ---------- Blog tile ---------- */
const BlogTile = ({ post }) => (
  <Link
    to={`/blog/${post.formattedTitle}`}
    className="group block bg-white rounded-lg shadow hover:shadow-lg transition overflow-hidden"
  >
    <img
      src={post.image}
      alt={post.title}
      className="h-44 w-full object-cover group-hover:scale-105 transition-transform duration-300"
    />
    <div className="p-3">
      <h3 className="text-sm font-bold leading-snug line-clamp-2 group-hover:underline">
        {post.title}
      </h3>
      <p className="mt-1 text-[11px] text-gray-500">
        {post.date}
        {post.author && ` · ${post.author}`}
      </p>
    </div>
  </Link>
);

/* ---------- Main section ---------- */
const LearnMore = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  /* fetch latest 4 */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const q = query(collection(db, 'blogPosts'), orderBy('date', 'desc'), limit(4));
        const snap = await getDocs(q);
        const storage = getStorage();

        const fetched = await Promise.all(
          snap.docs.map(async (docSnap) => {
            const data = docSnap.data();
            const title = data.title;
            const formattedTitle = title.replace(/[\s:]+/g, '_');
            const bannerPath = `blog_posts/images/${formattedTitle}/${formattedTitle}-banner.webp`;

            let image = '';
            try {
              image = await getDownloadURL(ref(storage, bannerPath));
            } catch {
              image = 'https://via.placeholder.com/400x250?text=No+image';
            }

            return {
              id: docSnap.id,
              title,
              formattedTitle,
              date: data.date,
              author: data.author,
              image,
            };
          })
        );

        setPosts(fetched);
      } catch (err) {
        console.error('Error loading blog previews:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section className="md:mt-12 rounded-2xl mx-auto px-4 md:px-12 py-12 md:w-11/12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        {/* Left column */}
        <div className="self-start">
          <h2 className="text-4xl md:text-4xl font-extrabold text-gray-900 mb-4">
            Learn More About<br />Hair Transplants
          </h2>
          <p className="text-xl md:text-lg text-gray-700 md:mb-4 border-b-2 md:border-none border-gray-300 pb-6 md:pb-0">
            Dive into our latest articles, guides, and opinion pieces to make
            confident decisions on your hair‑restoration journey.
          </p>

          {/* Desktop‑only button */}
          <Link
            to="/blog-showroom"
            className="hidden lg:inline-block bg-gray-900 text-white font-semibold px-6 py-3 rounded-lg"
          >
            Explore All Articles →
          </Link>
        </div>

        {/* 2 × 2 grid of posts */}
        <div className="mt-0 grid grid-cols-2 gap-4">
          {loading
            ? [...Array(4)].map((_, i) => <Skeleton key={i} />)
            : posts.map((p) => <BlogTile key={p.id} post={p} />)}
        </div>

        {/* Mobile‑only full‑width button */}
        <Link
          to="/blog-showroom"
          className="lg:hidden col-span-full bg-gray-900 text-white text-center font-semibold py-3 rounded-lg block w-full"
        >
          Explore All Articles
        </Link>
      </div>
    </section>
  );
};

export default LearnMore;
